import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Save, Send, Loader2, ShieldCheck } from "lucide-react";
import { resolveBackendBase } from "../lib/api";

const API_URL = `${resolveBackendBase()}/api/v2`;

export default function ManagerValidationPage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [responses, setResponses] = useState({});
    const [savingState, setSavingState] = useState("idle");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        axios.get(`${API_URL}/manager-validation/${token}`)
            .then((r) => {
                if (r.data.status === "submitted" || r.data.status === "scored") {
                    setSubmitted(true);
                    setData(r.data);
                } else {
                    setData(r.data);
                    setResponses(r.data.saved_responses || {});
                }
                setLoading(false);
            })
            .catch((e) => { setError(e.response?.data?.detail || "Failed to load manager validation"); setLoading(false); });
    }, [token]);

    const calcProgress = useCallback((resps, payload) => {
        const total = (payload?.form?.meta?.total_questions) || 0;
        if (total === 0) return 0;
        const answered = Object.values(resps).filter((v) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)).length;
        return Math.min(100, Math.round((answered / total) * 100));
    }, []);

    useEffect(() => {
        if (!data || submitted) return;
        const progress = calcProgress(responses, data);
        const t = setTimeout(async () => {
            if (Object.keys(responses).length === 0) return;
            setSavingState("saving");
            try {
                await axios.post(`${API_URL}/manager-validation/${token}/save`, { responses, progress_pct: progress });
                setSavingState("saved");
                setTimeout(() => setSavingState("idle"), 1500);
            } catch {
                setSavingState("error");
            }
        }, 1500);
        return () => clearTimeout(t);
    }, [responses, data, submitted, calcProgress, token]);

    const setAnswer = (qid, val) => setResponses((r) => ({ ...r, [qid]: val }));

    const submit = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/manager-validation/${token}/submit`, { responses, progress_pct: 100 });
            setSubmitted(true);
        } catch (e) {
            alert("Submit failed: " + (e.response?.data?.detail || e.message));
        }
        setSubmitting(false);
    };

    if (loading) return (
        <div className="min-h-screen bg-background flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="bg-card border border-border/40 p-8 max-w-md text-center" data-testid="mv-error">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
                <h1 className="text-lg font-display font-bold text-white mb-2">Unable to load validation</h1>
                <p className="text-sm text-gray-500">{error}</p>
            </div>
        </div>
    );

    if (submitted) return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="bg-card border border-border/40 p-10 max-w-md text-center" data-testid="mv-submitted">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h1 className="text-xl font-display font-bold text-white mb-2">Validation submitted</h1>
                <p className="text-sm text-gray-500 leading-relaxed">
                    Thank you. Your inputs are weighted at 30% of the capability composite and
                    feed directly into the Values Floor Rule. Scoring is in progress.
                </p>
            </div>
        </div>
    );

    const modules = data?.form?.modules || [];
    const progress = calcProgress(responses, data);
    const candidateName = data?.candidate?.name || "direct report";
    const roleTitle = data?.candidate?.job_title || "";

    return (
        <div className="min-h-screen bg-background" data-testid="mv-take-page">
            <header className="border-b border-border/40 bg-card sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-[2px] text-gray-600 mb-1">Manager Validation</p>
                        <h1 className="text-sm font-display font-bold text-white">{candidateName} · {roleTitle}</h1>
                        <p className="text-xs text-gray-600 font-mono">{data?.workspace?.name} &middot; {data?.cycle?.name}</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-xs text-gray-500 font-mono" data-testid="mv-progress-pct">{progress}% complete</div>
                        {savingState === "saving" && <div className="text-xs text-gray-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving</div>}
                        {savingState === "saved" && <div className="text-xs text-green-400 flex items-center gap-1" data-testid="mv-saved-indicator"><Save className="w-3 h-3" /> Saved</div>}
                    </div>
                </div>
                <div className="h-0.5 bg-border/40">
                    <div className="h-full bg-white transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                <div className="mb-8 border-l-2 border-white/20 pl-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck className="w-4 h-4 text-gray-500" />
                        <h2 className="text-xl font-display font-bold text-white">
                            Validate {candidateName.split(" ")[0] || "this role"}&rsquo;s scope
                        </h2>
                    </div>
                    <p className="text-sm text-gray-400 leading-relaxed">
                        Your inputs capture how the role actually operates on your team. Responses feed the
                        Manager Validation layer (30% weight) of the capability composite and the Values
                        Floor Rule. Answers are confidential; candidates do not see them. Auto-saves every
                        1.5 seconds.
                    </p>
                </div>

                {modules.length === 0 && (
                    <div className="bg-card border border-border/40 p-8 text-sm text-gray-500 text-center">
                        This validation has no questions configured yet. Please contact your workspace admin.
                    </div>
                )}

                {modules.map((mod, mi) => (
                    <section key={mi} className="mb-10" data-testid={`mv-module-${mod.module_id || mi}`}>
                        <h3 className="text-lg font-display font-bold text-white mb-1">{mod.module_name}</h3>
                        <p className="text-[10px] text-gray-600 font-mono uppercase tracking-[1.5px] mb-4">
                            respondent: {mod.respondent || "manager"}
                        </p>
                        {(mod.sections || []).map((sec, si) => (
                            <div key={si} className="bg-card border border-border/40 p-5 mb-3">
                                <h4 className="text-sm font-semibold text-white mb-1">{sec.section_title}</h4>
                                {sec.instructions && (
                                    <p className="text-xs text-gray-500 mb-4 leading-relaxed">{sec.instructions}</p>
                                )}
                                <div className="space-y-5">
                                    {(sec.questions || []).map((q) => (
                                        <MVQuestion key={q.id} q={q} value={responses[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                ))}

                {modules.length > 0 && (
                    <div className="sticky bottom-6 bg-card border border-border/40 p-4 flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                            Review responses before submitting. You can return later — auto-save is on.
                        </p>
                        <button onClick={submit} disabled={submitting}
                            className="px-5 py-2.5 bg-white text-black text-sm font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center gap-2"
                            data-testid="mv-submit-btn">
                            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting</> : <><Send className="w-4 h-4" /> Submit validation</>}
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
}


function MVQuestion({ q, value, onChange }) {
    const common = "w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-white/30 outline-none";
    return (
        <div data-testid={`mv-q-${q.id}`}>
            <label className="block text-sm text-gray-200 mb-2 leading-relaxed">{q.prompt}</label>
            {q.type === "text" || q.type === "scenario_with_followup" ? (
                <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${common} h-28 resize-none`} placeholder="Your response..." />
            ) : q.type === "likert_5" ? (
                <div className="flex gap-2">
                    {["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"].map((label, i) => (
                        <button key={i} type="button" onClick={() => onChange(i + 1)}
                            className={`flex-1 px-2 py-2 text-xs border transition-colors ${value === i + 1 ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white hover:border-white/30"}`}
                            data-testid={`mv-likert-${q.id}-${i + 1}`}>
                            {label}
                        </button>
                    ))}
                </div>
            ) : q.type === "multi_select" ? (
                <div className="flex flex-wrap gap-2">
                    {(q.options || []).map((opt) => {
                        const arr = Array.isArray(value) ? value : [];
                        const on = arr.includes(opt);
                        return (
                            <button key={opt} type="button" onClick={() => onChange(on ? arr.filter((x) => x !== opt) : [...arr, opt])}
                                className={`px-3 py-1.5 text-xs border transition-colors ${on ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white"}`}>
                                {opt}
                            </button>
                        );
                    })}
                </div>
            ) : q.type === "rating_grid" ? (
                <div className="flex gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <button key={n} type="button" onClick={() => onChange(n)}
                            className={`w-9 h-9 text-xs border transition-colors ${value === n ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white"}`}>
                            {n}
                        </button>
                    ))}
                </div>
            ) : (
                <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${common} h-20 resize-none`} placeholder="Your response..." />
            )}
        </div>
    );
}
